import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { CheckCircle2, XCircle, ArrowRight, RefreshCw, MessageSquare, CreditCard } from "lucide-react";
import API from "../api/api";
import { setUser, setCredits } from "../store/userSlice";
import "../style/payment-status.css";

export default function PaymentCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const merchantTransactionId = searchParams.get("merchantTransactionId") || searchParams.get("transactionId");

  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("PENDING"); // PENDING, SUCCESS, FAILED
  const [orderDetails, setOrderDetails] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const pollCountRef = useRef(0);

  const checkStatus = async () => {
    if (!merchantTransactionId) {
      setLoading(false);
      setStatus("FAILED");
      setErrorMessage("No merchant transaction ID found in redirect.");
      return;
    }

    try {
      const res = await API.get(`/payment/phonepe/status/${merchantTransactionId}`);
      const data = res.data;

      if (data.isFulfilled || data.gatewayStatus === "PAYMENT_SUCCESS") {
        setStatus("SUCCESS");
        setOrderDetails(data.order);
        setLoading(false);

        // Refresh user profile & credits in Redux
        try {
          const meRes = await API.get("/auth/me");
          if (meRes.data?.data) {
            dispatch(setUser(meRes.data.data));
            if (meRes.data.data.credits !== undefined) {
              dispatch(setCredits(meRes.data.data.credits));
            }
          }
        } catch (e) {
          console.error("Failed to refresh user profile:", e);
        }
      } else if (
        data.gatewayStatus === "PAYMENT_ERROR" ||
        data.gatewayStatus === "PAYMENT_DECLINED" ||
        data.gatewayStatus === "TIMED_OUT"
      ) {
        setStatus("FAILED");
        setOrderDetails(data.order);
        setErrorMessage("Payment was declined or encountered an error.");
        setLoading(false);
      } else {
        // Still pending at PhonePe - poll up to 5 times (every 3s)
        if (pollCountRef.current < 5) {
          pollCountRef.current += 1;
          setTimeout(checkStatus, 3000);
        } else {
          setStatus("PENDING");
          setLoading(false);
        }
      }
    } catch (err) {
      console.error("Status check error:", err);
      if (pollCountRef.current < 3) {
        pollCountRef.current += 1;
        setTimeout(checkStatus, 3000);
      } else {
        setStatus("FAILED");
        setErrorMessage(err.response?.data?.message || "Failed to confirm payment status.");
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    checkStatus();
  }, [merchantTransactionId]);

  return (
    <div className="payment-status-container">
      <div className="payment-status-card">
        {loading ? (
          <>
            <div className="payment-status-icon-wrapper loading">
              <div className="status-spinner" />
            </div>
            <h2 className="payment-status-title">Verifying Payment...</h2>
            <p className="payment-status-subtitle">
              Please wait while we confirm your transaction securely with PhonePe.
            </p>
          </>
        ) : status === "SUCCESS" ? (
          <>
            <div className="payment-status-icon-wrapper success">
              <CheckCircle2 size={54} />
            </div>
            <h2 className="payment-status-title">Payment Successful!</h2>
            <p className="payment-status-subtitle">
              Your subscription has been activated and credits have been credited to your account.
            </p>

            {orderDetails && (
              <div className="payment-receipt-details">
                <div className="receipt-row">
                  <span className="receipt-label">Plan Purchased</span>
                  <span className="receipt-val">{orderDetails.planName}</span>
                </div>
                <div className="receipt-row">
                  <span className="receipt-label">Credits Added</span>
                  <span className="receipt-val highlight">+{orderDetails.credits?.toLocaleString()} Credits</span>
                </div>
                <div className="receipt-row">
                  <span className="receipt-label">Amount Paid</span>
                  <span className="receipt-val">₹{orderDetails.amount}</span>
                </div>
                <div className="receipt-row">
                  <span className="receipt-label">Transaction ID</span>
                  <span className="receipt-val">{orderDetails.merchantTransactionId}</span>
                </div>
              </div>
            )}

            <div className="payment-status-actions">
              <Link to="/" className="btn-primary-action">
                <MessageSquare size={18} style={{ marginRight: 8 }} /> Start Chatting
              </Link>
              <Link to="/account" className="btn-secondary-action">
                <CreditCard size={18} style={{ marginRight: 8 }} /> View Account
              </Link>
            </div>
          </>
        ) : status === "FAILED" ? (
          <>
            <div className="payment-status-icon-wrapper error">
              <XCircle size={54} />
            </div>
            <h2 className="payment-status-title">Payment Failed</h2>
            <p className="payment-status-subtitle">
              {errorMessage || "Your payment could not be processed. No money was deducted, or it will be refunded."}
            </p>

            {merchantTransactionId && (
              <div className="payment-receipt-details">
                <div className="receipt-row">
                  <span className="receipt-label">Transaction Ref</span>
                  <span className="receipt-val">{merchantTransactionId}</span>
                </div>
              </div>
            )}

            <div className="payment-status-actions">
              <Link to="/subscription" className="btn-primary-action">
                <RefreshCw size={18} style={{ marginRight: 8 }} /> Try Again
              </Link>
              <Link to="/" className="btn-secondary-action">
                Back to Home
              </Link>
            </div>
          </>
        ) : (
          <>
            <div className="payment-status-icon-wrapper loading">
              <RefreshCw size={44} className="animate-spin" />
            </div>
            <h2 className="payment-status-title">Payment Processing</h2>
            <p className="payment-status-subtitle">
              PhonePe is still processing your payment. Your balance will be updated automatically once confirmed.
            </p>

            <div className="payment-status-actions">
              <button onClick={() => { setLoading(true); pollCountRef.current = 0; checkStatus(); }} className="btn-primary-action">
                <RefreshCw size={18} style={{ marginRight: 8 }} /> Check Status Again
              </button>
              <Link to="/" className="btn-secondary-action">
                Go Home
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
